import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Collapse from "@mui/material/Collapse";
import Typography from "@mui/material/Typography";
import { type ReactNode, useState } from "react";

/**
 * A micro accordion: side details one line each, opened on click (a company's
 * figures, its tech in a drawer), without a box around them. Holds `FoldRow`s.
 */
export function FoldList({ children }: { children: ReactNode }) {
  return <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>{children}</Box>;
}

/** A small solid triangle: right while folded, turning down when open. */
function Caret({ open }: { open: boolean }) {
  return (
    <Box
      component="svg"
      viewBox="0 0 8 8"
      aria-hidden
      sx={{
        width: 8,
        height: 8,
        flexShrink: 0,
        color: open ? "text.primary" : "text.secondary",
        transition: (t) =>
          t.transitions.create(["transform", "color"], { duration: t.transitions.duration.shorter }),
        transform: open ? "rotate(90deg)" : "none",
      }}
    >
      <path d="M2 0.75 L6.75 4 L2 7.25 Z" fill="currentColor" />
    </Box>
  );
}

interface FoldRowProps {
  label: ReactNode;
  /** Grey, after the label, one line: the gist while folded. */
  summary?: ReactNode;
  expanded: boolean;
  onToggle: () => void;
  /** Mounted only while open, so its queries run on first open. */
  children: ReactNode;
}

export function FoldRow({ label, summary, expanded, onToggle, children }: FoldRowProps) {
  return (
    <Box>
      <ButtonBase
        onClick={onToggle}
        aria-expanded={expanded}
        sx={{
          width: "100%",
          justifyContent: "flex-start",
          gap: 1,
          px: 0.75,
          py: 0.5,
          borderRadius: 1,
          textAlign: "left",
          "&:hover": { bgcolor: "action.hover" },
          "&.Mui-focusVisible": { outline: 2, outlineColor: "primary.main", outlineOffset: -2 },
        }}
      >
        <Caret open={expanded} />
        <Typography variant="body2" sx={{ fontWeight: 500, flexShrink: 0 }}>
          {label}
        </Typography>
        {summary != null && !expanded && (
          <Typography variant="body2" noWrap component="div" sx={{ color: "text.secondary", minWidth: 0 }}>
            {summary}
          </Typography>
        )}
      </ButtonBase>
      <Collapse in={expanded} unmountOnExit>
        {/* Hangs off the caret: a thin guide down its centre, content in line with the label. */}
        <Box sx={{ ml: "9px", pl: "12px", pr: 0.75, pt: 0.5, pb: 1, borderLeft: 1, borderColor: "divider" }}>
          {children}
        </Box>
      </Collapse>
    </Box>
  );
}

/**
 * Which of a list's folds are open, all folded at first and again whenever
 * `resetKey` changes (a drawer stepping to the next record keeps its mount).
 */
export function useOpenFolds(resetKey: string) {
  const [open, setOpen] = useState<string[]>([]);
  const [openFor, setOpenFor] = useState(resetKey);
  if (openFor !== resetKey) {
    setOpenFor(resetKey);
    setOpen([]);
  }
  const current = openFor === resetKey ? open : [];
  return {
    isOpen: (key: string) => current.includes(key),
    toggle: (key: string) => setOpen((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key])),
  };
}

interface AdvancedFoldProps {
  /** Grey gist while folded: what the options are set to now. */
  summary?: ReactNode;
  /** Start open, e.g. when an option already differs from its default. */
  defaultExpanded?: boolean;
  children: ReactNode;
}

/**
 * "Advanced": the options people rarely change (a model choice), folded away.
 * Always the last thing in its form, directly above the actions.
 */
export function AdvancedFold({ summary, defaultExpanded = false, children }: AdvancedFoldProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  return (
    <FoldList>
      <FoldRow label="Advanced" summary={summary} expanded={expanded} onToggle={() => setExpanded((v) => !v)}>
        {children}
      </FoldRow>
    </FoldList>
  );
}
